import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import { formatDate, formatFileSize } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  ArrowLeft, Download, Upload, Package, History, CheckCircle, XCircle, Loader2, Image, Clock,
  AlertTriangle, Zap, ListFilter
} from 'lucide-react';
import { toast } from 'sonner';
import OutputsPanel from '../components/OutputsPanel';

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [stats, setStats] = useState(null);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [outputsVersion, setOutputsVersion] = useState(0);
  const [settings, setSettings] = useState({
    name: '',
    source_url: '',
    auto_import_enabled: false,
    auto_import_time: '03:00',
    auto_publish_enabled: true,
  });

  const fetchData = useCallback(async () => {
    try {
      const [projectRes, statsRes, versionsRes] = await Promise.all([
        api.get(`/projects/${projectId}`),
        api.get(`/projects/${projectId}/stats`),
        api.get(`/projects/${projectId}/versions`),
      ]);
      setProject(projectRes.data);
      setStats(statsRes.data);
      setVersions(versionsRes.data);
      setSettings({
        name: projectRes.data.name,
        source_url: projectRes.data.source_url,
        auto_import_enabled: projectRes.data.auto_import_enabled || false,
        auto_import_time: projectRes.data.auto_import_time || '03:00',
        auto_publish_enabled: projectRes.data.auto_publish_enabled !== false,
      });
    } catch (err) {
      toast.error('Nepodařilo se načíst projekt');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await api.post(`/projects/${projectId}/import`);
      const { new: added, updated, removed, total } = res.data;
      toast.success(`Import dokončen: ${added} nových, ${updated} aktualizovaných, ${removed} odstraněných (celkem ${total})`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import se nezdařil');
    } finally {
      setImporting(false);
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    try {
      const res = await api.post(`/projects/${projectId}/publish`);
      const parts = (res.data.outputs || []).map(o => `${o.output_name}: ${o.product_count} položek`);
      toast.success(`Publikováno — ${parts.join(', ')}`);
      if (res.data.errors?.length) {
        toast.error(res.data.errors.map(e => `${e.output_name}: ${e.error}`).join('; '));
      }
      fetchData();
      setOutputsVersion(v => v + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Publikování se nezdařilo');
    } finally {
      setPublishing(false);
    }
  };

  const handleRollback = async (version) => {
    if (!window.confirm(`Vrátit kanál ${version.output_name || ''} na verzi v${version.version_number}? Veřejná URL začne hned vracet její obsah.`)) return;
    try {
      const res = await api.post(`/projects/${projectId}/outputs/${version.output_id}/rollback`, {
        version_number: version.version_number,
      });
      toast.success(res.data.message);
      fetchData();
      setOutputsVersion(v => v + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Návrat se nepodařil');
    }
  };

  // Příští běh podle nastaveného času: dnes, pokud ještě nebyl, jinak zítra
  const nextRunLabel = () => {
    const [h, m] = (settings.auto_import_time || '03:00').split(':').map(Number);
    const next = new Date();
    next.setHours(h, m, 0, 0);
    const lastImport = project?.last_import ? new Date(project.last_import) : null;
    if (next <= new Date() && (!lastImport || lastImport >= next)) {
      next.setDate(next.getDate() + 1);
    } else if (next <= new Date()) {
      return 'během několika minut';
    }
    const today = next.toDateString() === new Date().toDateString();
    return `${today ? 'dnes' : 'zítra'} v ${settings.auto_import_time}`;
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}`, settings);
      toast.success('Nastavení uloženo');
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map(d => d.msg).join('; ') : (detail || 'Nepodařilo se uložit nastavení'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <Skeleton className="h-8 w-48 mb-8" />
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="p-6 lg:p-8 text-center">
        <p className="text-slate-500">Projekt nenalezen</p>
        <Button asChild variant="outline" className="mt-4"><Link to="/">Zpět na přehled</Link></Button>
      </div>
    );
  }

  const statTiles = [
    { label: 'Produktů', value: stats?.total_products || 0, icon: Package, color: 'text-amber-500' },
    { label: 'Aktivních', value: stats?.active_products || 0, icon: CheckCircle, color: 'text-green-500' },
    { label: 'Skladem', value: stats?.in_stock_products || 0, icon: Package, color: 'text-blue-500' },
    { label: 'Poslední publikace', value: project.last_publish ? formatDate(project.last_publish) : '—', icon: History, color: 'text-purple-500', small: true },
  ];

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center gap-4 mb-6">
        <Button asChild variant="ghost" size="icon" data-testid="back-btn">
          <Link to="/"><ArrowLeft className="w-5 h-5" /></Link>
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-heading font-bold text-slate-900 truncate">{project.name}</h1>
          <p className="text-sm text-slate-500 truncate">{project.source_url}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" onClick={handleImport} disabled={importing} data-testid="import-btn">
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            {importing ? 'Importuji...' : 'Importovat'}
          </Button>
          <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900" onClick={handlePublish}
            disabled={publishing || stats?.total_products === 0} data-testid="publish-btn">
            {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            {publishing ? 'Publikuji...' : 'Publikovat všechny kanály'}
          </Button>
        </div>
      </div>

      {project.last_import_error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-800">Poslední import selhal</p>
            <p className="text-xs text-red-600 mt-0.5">{project.last_import_error}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {statTiles.map(tile => (
          <Card key={tile.label} className="border border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">{tile.label}</p>
                  <p className={`font-heading font-bold text-slate-900 ${tile.small ? 'text-base mt-1' : 'text-2xl'}`}>{tile.value}</p>
                </div>
                <tile.icon className={`w-8 h-8 ${tile.color}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview" data-testid="tab-overview">Přehled</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Historie</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Nastavení</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Button asChild variant="outline" data-testid="products-list-btn">
              <Link to={`/projects/${projectId}/products`}><ListFilter className="w-4 h-4 mr-2" />Produkty</Link>
            </Button>
            <Button asChild variant="outline" data-testid="rules-btn">
              <Link to={`/projects/${projectId}/rules`}><Zap className="w-4 h-4 mr-2" />Pravidla</Link>
            </Button>
            <Button asChild variant="outline" data-testid="images-btn">
              <Link to={`/projects/${projectId}/images`}><Image className="w-4 h-4 mr-2" />Obrázky</Link>
            </Button>
          </div>
          <OutputsPanel key={outputsVersion} projectId={projectId} onPublished={fetchData} />
          {project.last_import && (
            <p className="text-xs text-slate-400">Poslední import: {formatDate(project.last_import)}</p>
          )}
        </TabsContent>

        <TabsContent value="history">
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Historie publikací</CardTitle>
              <CardDescription>Posledních 30 verzí každého kanálu. Verzi s uloženým souborem jde vrátit zpět.</CardDescription>
            </CardHeader>
            <CardContent>
              {versions.length === 0 ? (
                <p className="text-center text-slate-500 py-8">Zatím žádné publikace</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 text-slate-500">
                        <th className="text-left py-3 px-4 font-medium">Verze</th>
                        <th className="text-left py-3 px-4 font-medium">Kanál</th>
                        <th className="text-left py-3 px-4 font-medium">Datum</th>
                        <th className="text-left py-3 px-4 font-medium">Položek</th>
                        <th className="text-left py-3 px-4 font-medium">Velikost</th>
                        <th className="text-left py-3 px-4 font-medium">Stav</th>
                        <th className="py-3 px-4"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {versions.map(version => (
                        <tr key={version.id} className="border-b border-slate-100 hover:bg-amber-50/50" data-testid={`version-row-${version.version_number}`}>
                          <td className="py-3 px-4 font-medium">v{version.version_number}</td>
                          <td className="py-3 px-4 text-slate-600">
                            {version.output_name || 'Meta (Facebook a Instagram)'}
                            {version.note && <div className="text-xs text-slate-400">{version.note}</div>}
                          </td>
                          <td className="py-3 px-4 text-slate-600">{formatDate(version.created_at)}</td>
                          <td className="py-3 px-4">{version.product_count}</td>
                          <td className="py-3 px-4">{formatFileSize(version.file_size)}</td>
                          <td className="py-3 px-4">
                            {version.status === 'OK' ? (
                              <Badge className="bg-green-100 text-green-800 border-green-200"><CheckCircle className="w-3 h-3 mr-1" />OK</Badge>
                            ) : (
                              <span title={version.error || ''}>
                                <Badge className="bg-red-100 text-red-800 border-red-200"><XCircle className="w-3 h-3 mr-1" />Chyba</Badge>
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-right">
                            {version.has_file && version.output_id && (
                              <Button size="sm" variant="ghost" className="text-xs" onClick={() => handleRollback(version)} data-testid={`rollback-${version.version_number}`}>
                                <History className="w-3.5 h-3.5 mr-1" /> Vrátit
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-heading">Nastavení projektu</CardTitle>
              <CardDescription>Zdroj dat a automatická aktualizace</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="settings-name">Název projektu</Label>
                <Input id="settings-name" value={settings.name} maxLength={100}
                  onChange={e => setSettings({ ...settings, name: e.target.value })} data-testid="settings-name-input" />
                <p className="text-xs text-slate-400">Přejmenování nezmění URL kanálů.</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="settings-url">URL zdrojového feedu</Label>
                <Input id="settings-url" value={settings.source_url} placeholder="https://..."
                  onChange={e => setSettings({ ...settings, source_url: e.target.value })} data-testid="settings-url-input" />
              </div>

              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="auto-import" className="text-base font-medium flex items-center gap-2">
                      <Clock className="w-4 h-4 text-amber-500" /> Denní aktualizace
                    </Label>
                    <p className="text-sm text-slate-500 mt-1">Každý den stáhnout zdrojový feed a publikovat všechny kanály.</p>
                  </div>
                  <Switch id="auto-import" checked={settings.auto_import_enabled}
                    onCheckedChange={checked => setSettings({ ...settings, auto_import_enabled: checked, auto_publish_enabled: true })}
                    data-testid="auto-import-switch" />
                </div>

                {settings.auto_import_enabled && (
                  <>
                    <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-slate-200">
                      <Label htmlFor="import-time" className="text-sm whitespace-nowrap">Každý den v:</Label>
                      <select id="import-time" value={settings.auto_import_time}
                        onChange={e => setSettings({ ...settings, auto_import_time: e.target.value })}
                        className="flex h-9 rounded-md border border-input bg-white px-3 py-1 text-sm" data-testid="import-time-select">
                        {Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`).map(t => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                      <span className="text-xs text-slate-500">Když stažení selže, zkusí se znovu (max 3× po 30 minutách).</span>
                    </div>
                    <div className="text-sm text-slate-600 space-y-1 pt-2 border-t border-slate-200">
                      <div>Poslední import: <span className="text-slate-900">{project.last_import ? formatDate(project.last_import) : 'zatím nikdy'}</span></div>
                      <div>Poslední publikace: <span className="text-slate-900">{project.last_publish ? formatDate(project.last_publish) : 'zatím nikdy'}</span></div>
                      <div>Další běh: <span className="text-slate-900">{nextRunLabel()}</span></div>
                      {project.last_publish_error && <div className="text-red-600">Poslední publikace selhala: {project.last_publish_error}</div>}
                    </div>
                  </>
                )}
              </div>

              <Button onClick={handleSaveSettings} className="bg-amber-500 hover:bg-amber-600 text-slate-900" disabled={saving} data-testid="save-settings-btn">
                {saving ? 'Ukládám...' : 'Uložit nastavení'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
